// Requests for Vipupisteiden valinta (vipupiste_focus.v1). Paths come from API_ENDPOINTS (config/api.js).
import { useCallback, useEffect, useState } from 'react';
import API_ENDPOINTS from '../../config/api';
import useContentRefresh from '../../hooks/useContentRefresh';
import { getOrCreateParticipantId } from '../../utils/participantId';
import socketService from '../../../../../packages/shared/socket';

export const RESOURCE = 'vipupiste-focus';

/**
 * JSON request with the session cookie and this device's participant id (the
 * vote counts per device); throws the server's error message and code.
 */
export async function request(url, { method = 'GET', body } = {}) {
  const participantId = getOrCreateParticipantId();
  const res = await fetch(url, {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(participantId ? { 'X-Participant-Id': participantId } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    // The module's routes send { error: { code, message } }; the role check sends { error: string }.
    const err = new Error(data.error?.message || (typeof data.error === 'string' ? data.error : `HTTP ${res.status}`));
    err.code = data.error?.code;
    throw err;
  }
  return data;
}

// A backstop for what emits no event: the mode and budget are instance
// settings, saved by the shared module controls.
const POLL_MS = 15000;

/**
 * The module's state, reloaded when any device votes or the facilitator locks
 * (instance-content-changed, resource 'vipupiste-focus').
 */
export function useFocusState(instanceId) {
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    if (!instanceId) return;
    try {
      setState(await request(API_ENDPOINTS.VIPUPISTE_FOCUS(instanceId)));
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, [instanceId]);
  // Joins the instance's room before useContentRefresh subscribes: that hook
  // listens only on a socket that already exists.
  useEffect(() => {
    if (instanceId) socketService.connect(instanceId, 'display', `vipupiste-focus-${getOrCreateParticipantId() || Date.now()}`);
  }, [instanceId]);
  useEffect(() => {
    load();
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [load]);
  useContentRefresh(instanceId, [RESOURCE], load, { minIntervalMs: 1000, enabled: Boolean(instanceId) });
  return { state, setState, error, reload: load };
}
